import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AdminShell from "@/components/AdminShell";
import CompModal from "@/components/admin/CompModal";
import api, { API } from "@/lib/api";
import {
  Gift,
  Clock,
  Mail,
  Lock,
  Unlock,
  RefreshCw,
  UserX,
  ExternalLink,
  Download,
  LogIn,
  KeyRound,
  Send,
} from "lucide-react";

const TABS = [
  ["overview", "Overview"],
  ["activity", "Activity history"],
  ["projects", "Projects"],
  ["billing", "Billing"],
  ["comps", "Comps"],
  ["notes", "Notes"],
];

export default function AdminUserDetail() {
  const { user_id } = useParams();
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("overview");
  const [showComp, setShowComp] = useState(false);
  const [reasonPrompt, setReasonPrompt] = useState(null);
  const [toast, setToast] = useState(null);

  const load = async () => {
    const r = await api.get(`/admin/users/${user_id}`);
    setDetail(r.data);
  };

  useEffect(() => {
    document.title = "Admin · User detail";
    load().catch(() => setDetail({ error: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user_id]);

  if (!detail) {
    return (
      <AdminShell>
        <div className="text-sm text-zinc-500">Loading user…</div>
      </AdminShell>
    );
  }
  if (detail.error) {
    return (
      <AdminShell>
        <div className="text-sm text-rose-400">User not found.</div>
      </AdminShell>
    );
  }

  const u = detail.user;
  const summary = detail.summary;

  const doAction = (type) => setReasonPrompt({ type });

  const run = async (type, reason, extra = {}) => {
    try {
      if (type === "trial_extend") {
        await api.post(`/admin/users/${user_id}/trial-extend`, { ...extra, reason });
      } else if (type === "lock") {
        await api.post(`/admin/users/${user_id}/lock`, { reason });
      } else if (type === "unlock") {
        await api.post(`/admin/users/${user_id}/unlock`, { reason });
      } else if (type === "reset_password") {
        await api.post(`/admin/users/${user_id}/reset-password`, { reason });
      } else if (type === "soft_delete") {
        await api.post(`/admin/users/${user_id}/soft-delete`, {
          reason,
          confirm: extra.confirm,
        });
      } else if (type === "impersonate") {
        await api.post(`/admin/users/${user_id}/impersonate`, { reason });
        window.location.href = "/dashboard";
        return;
      } else if (type === "send_email") {
        await api.post(`/admin/users/${user_id}/send-email`, {
          template_id: extra.template_id,
          reason,
        });
      }
      setToast(`${type} → done`);
      setReasonPrompt(null);
      load();
    } catch (e) {
      setToast(e?.response?.data?.detail || "Failed");
    } finally {
      setTimeout(() => setToast(null), 2000);
    }
  };

  const gravatar = u?.picture || "";

  return (
    <AdminShell>
      {/* HEADER */}
      <div className="flex items-start gap-4 mb-6" data-testid="user-header">
        {gravatar ? (
          <img
            src={gravatar}
            alt={u.name}
            className="w-14 h-14 rounded-full border border-zinc-700"
          />
        ) : (
          <div className="w-14 h-14 rounded-full bg-zinc-800 flex items-center justify-center display font-black text-lg">
            {(u.name || u.email || "").slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="display text-2xl font-black tracking-tight">
              {u.name || u.email}
            </h1>
            <PlanBadge plan={u.plan} comp={!!u.original_plan_before_comp} />
            <StatusPill status={u.account_status || "active"} />
            {u.is_admin && <span className="text-[10px] mono uppercase tracking-[0.16em] px-1.5 py-0.5 border border-amber-500 text-amber-400 rounded-sm">admin</span>}
          </div>
          <div className="mt-1 text-xs text-zinc-400 mono">{u.email}</div>
          <div className="mt-2 flex gap-4 text-[11px] text-zinc-500 mono uppercase tracking-[0.16em]">
            <span>Joined {u.created_at?.slice(0, 10)}</span>
            <span>· LTV £{summary.mrr_contribution_gbp}</span>
            <span>
              ·{" "}
              {u.last_login_at
                ? `${daysAgo(u.last_login_at)}d since login`
                : "Never logged in"}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap justify-end">
          <ActionBtn icon={Gift} label="Comp" onClick={() => setShowComp(true)} testid="action-comp" />
          <ActionBtn icon={Clock} label="Extend trial" onClick={() => doAction("trial_extend")} testid="action-trial" />
          <ActionBtn icon={RefreshCw} label="Refund" onClick={() => window.open("https://dashboard.stripe.com", "_blank")} testid="action-refund" />
          <ActionBtn icon={KeyRound} label="Reset pw" onClick={() => doAction("reset_password")} testid="action-reset" />
          {u.account_status === "locked" ? (
            <ActionBtn icon={Unlock} label="Unlock" onClick={() => doAction("unlock")} testid="action-unlock" />
          ) : (
            <ActionBtn icon={Lock} label="Lock" onClick={() => doAction("lock")} testid="action-lock" />
          )}
          <ActionBtn icon={LogIn} label="Log in as" onClick={() => doAction("impersonate")} testid="action-impersonate" />
          <ActionBtn icon={Send} label="Send email" onClick={() => doAction("send_email")} testid="action-email" />
          <ActionBtn icon={UserX} label="Delete" onClick={() => doAction("soft_delete")} testid="action-delete" danger />
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-zinc-800 flex gap-4 mb-4 text-xs mono uppercase tracking-[0.16em]">
        {TABS.map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`tab-${k}`}
            className={`py-2 border-b-2 ${
              tab === k
                ? "border-white text-white"
                : "border-transparent text-zinc-500 hover:text-zinc-200"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {/* TAB BODIES */}
      {tab === "overview" && <Overview summary={summary} recent={detail.recent_activity} projects={detail.projects} />}
      {tab === "activity" && <ActivityTab userId={user_id} />}
      {tab === "projects" && <ProjectsTab userId={user_id} />}
      {tab === "billing" && <BillingTab userId={user_id} />}
      {tab === "comps" && <CompsTab userId={user_id} onRevoke={load} />}
      {tab === "notes" && <NotesTab userId={user_id} />}

      {showComp && (
        <CompModal
          userId={user_id}
          userEmail={u.email}
          onClose={(saved) => {
            setShowComp(false);
            if (saved) load();
          }}
        />
      )}
      {reasonPrompt && (
        <ReasonPrompt
          kind={reasonPrompt.type}
          onClose={() => setReasonPrompt(null)}
          onConfirm={(reason, extra) => run(reasonPrompt.type, reason, extra)}
          userEmail={u.email}
        />
      )}
      {toast && (
        <div
          className="fixed bottom-4 right-4 bg-zinc-900 border border-zinc-700 rounded-[3px] px-3 py-2 text-xs"
          data-testid="action-toast"
        >
          {toast}
        </div>
      )}
    </AdminShell>
  );
}

function ActionBtn({ icon: Icon, label, onClick, testid, danger }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-[3px] border ${
        danger
          ? "border-rose-800 text-rose-300 hover:bg-rose-900/30"
          : "border-zinc-800 text-zinc-300 hover:bg-zinc-900"
      }`}
    >
      <Icon size={11} /> {label}
    </button>
  );
}

// ------- Overview -------
function Overview({ summary, recent, projects }) {
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="user-summary-cards">
        <SumCard label="Projects" value={summary.total_projects} />
        <SumCard label="Prompts" value={summary.total_prompts} />
        <SumCard label="Runs" value={summary.total_runs} />
        <SumCard label="Actions done" value={summary.actions_completed} />
        <SumCard label="Artefacts" value={summary.artefacts_generated} />
        <SumCard label="LLM cost" value={`£${summary.llm_cost_gbp}`} />
        <SumCard label="MRR" value={`£${summary.mrr_contribution_gbp}`} />
        <SumCard
          label="Profit margin"
          value={`£${summary.profit_margin_gbp.toFixed(2)}`}
          danger={summary.profit_margin_gbp < 0}
        />
      </div>

      <div className="mt-4 grid md:grid-cols-2 gap-3">
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-[4px]">
          <div className="px-3 py-2 border-b border-zinc-800 mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
            Recent activity
          </div>
          <div className="divide-y divide-zinc-800">
            {recent.length === 0 && (
              <div className="p-3 text-xs text-zinc-500">No activity yet.</div>
            )}
            {recent.map((e) => (
              <div key={e.activity_id} className="p-2 text-xs">
                <div>{e.description}</div>
                <div className="mono text-[10px] uppercase tracking-[0.14em] text-zinc-500 mt-0.5">
                  {e.event_type} · {shortDate(e.created_at)}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-[4px]">
          <div className="px-3 py-2 border-b border-zinc-800 mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
            Projects
          </div>
          <div className="divide-y divide-zinc-800">
            {projects.length === 0 && (
              <div className="p-3 text-xs text-zinc-500">No projects yet.</div>
            )}
            {projects.map((p) => (
              <div key={p.project_id} className="p-2 text-xs">
                <div className="font-medium">{p.name}</div>
                <div className="mono text-[10px] uppercase tracking-[0.14em] text-zinc-500 mt-0.5">
                  {p.industry || "—"} · {(p.competitors || []).length} competitors · {(p.languages || []).length} langs
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function SumCard({ label, value, danger }) {
  return (
    <div className={`bg-zinc-900/60 border rounded-[4px] p-3 ${danger ? "border-rose-700" : "border-zinc-800"}`}>
      <div className="mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
        {label}
      </div>
      <div className={`display text-lg font-black mt-0.5 tabular-nums ${danger ? "text-rose-300" : ""}`}>
        {value}
      </div>
    </div>
  );
}

// ------- Activity -------
function ActivityTab({ userId }) {
  const [events, setEvents] = useState([]);
  const [type, setType] = useState("");
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const limit = 50;

  const load = async (reset = false) => {
    const off = reset ? 0 : offset;
    const r = await api.get(
      `/admin/users/${userId}/activity?${type ? `event_type=${type}&` : ""}limit=${limit}&offset=${off}`
    );
    setEvents(reset ? r.data.events : [...events, ...r.data.events]);
    setTotal(r.data.total);
    setOffset(off + r.data.events.length);
  };

  useEffect(() => {
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, userId]);

  const eventTypes = useMemo(
    () => Array.from(new Set(events.map((e) => e.event_type))).sort(),
    [events]
  );

  return (
    <div data-testid="activity-tab">
      <div className="flex items-center gap-2 mb-3">
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          data-testid="activity-filter-type"
          className="bg-zinc-950 border border-zinc-800 rounded-[3px] px-2 py-1 text-xs"
        >
          <option value="">All event types</option>
          {eventTypes.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <a
          href={`${API}/admin/users/${userId}/activity.csv`}
          className="inline-flex items-center gap-1 bg-zinc-900 border border-zinc-700 text-xs px-2 py-1 rounded-[3px] hover:bg-zinc-800"
          data-testid="activity-export"
        >
          <Download size={11} /> Export CSV
        </a>
        <span className="text-[10px] text-zinc-500 mono uppercase tracking-[0.16em] ml-auto">
          {events.length} / {total}
        </span>
      </div>
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-[4px] divide-y divide-zinc-800">
        {events.length === 0 && (
          <div className="p-3 text-xs text-zinc-500">No activity yet.</div>
        )}
        {events.map((e) => (
          <div key={e.activity_id} className="p-2 text-xs">
            <div className="flex items-center gap-2">
              <span className="mono text-[10px] uppercase tracking-[0.14em] text-zinc-400 w-28 shrink-0">
                {e.event_type}
              </span>
              <span className="flex-1">{e.description}</span>
              <span className="mono text-[10px] text-zinc-500">
                {shortDate(e.created_at)}
              </span>
            </div>
          </div>
        ))}
      </div>
      {events.length < total && (
        <button
          onClick={() => load(false)}
          data-testid="activity-load-more"
          className="mt-3 w-full py-2 text-xs text-zinc-400 hover:text-white border border-zinc-800 rounded-[3px]"
        >
          Load more
        </button>
      )}
    </div>
  );
}

function ProjectsTab({ userId }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get(`/admin/users/${userId}/projects`).then((r) => setRows(r.data));
  }, [userId]);
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-[4px]" data-testid="projects-tab">
      <table className="w-full text-xs">
        <thead className="bg-zinc-900">
          <tr className="mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
            <th className="text-left p-2">Brand</th>
            <th className="text-left p-2">Industry</th>
            <th className="text-right p-2">Prompts</th>
            <th className="text-right p-2">Runs</th>
            <th className="text-left p-2">Last run</th>
            <th className="text-right p-2">AEO score</th>
            <th className="text-left p-2">Competitors</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-800">
          {rows.length === 0 && (
            <tr>
              <td colSpan={7} className="p-3 text-center text-zinc-500">
                No projects.
              </td>
            </tr>
          )}
          {rows.map((r) => (
            <tr key={r.project_id}>
              <td className="p-2 font-medium">{r.name}</td>
              <td className="p-2 text-zinc-400">{r.industry || "—"}</td>
              <td className="p-2 text-right mono tabular-nums">{r.prompt_count}</td>
              <td className="p-2 text-right mono tabular-nums">{r.run_count}</td>
              <td className="p-2 mono text-[10px] text-zinc-500">
                {r.last_run_at?.slice(0, 10) || "—"}
              </td>
              <td className="p-2 text-right mono tabular-nums">
                {r.aeo_score ?? "—"}
              </td>
              <td className="p-2 text-zinc-400 text-[11px]">
                {(r.competitors || []).slice(0, 3).join(", ")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BillingTab({ userId }) {
  const [bill, setBill] = useState(null);
  useEffect(() => {
    api.get(`/admin/users/${userId}/billing`).then((r) => setBill(r.data));
  }, [userId]);
  if (!bill) return <div className="text-xs text-zinc-500">Loading…</div>;
  return (
    <div data-testid="billing-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        <SumCard label="LTV" value={`£${bill.ltv_gbp}`} />
        <SumCard label="Invoices" value={bill.invoices.length} />
        <SumCard label="Refunds" value={bill.refunds.length} />
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-[4px] p-3 flex items-center justify-between">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
              Stripe
            </div>
            <div className="display text-sm mt-0.5 truncate">
              {bill.stripe_customer_id || "—"}
            </div>
          </div>
          {bill.stripe_customer_url && (
            <a
              href={bill.stripe_customer_url}
              target="_blank"
              rel="noreferrer"
              className="text-zinc-400 hover:text-white"
            >
              <ExternalLink size={12} />
            </a>
          )}
        </div>
      </div>
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-[4px]">
        <div className="px-3 py-2 border-b border-zinc-800 mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
          Invoices
        </div>
        {bill.invoices.length === 0 ? (
          <div className="p-3 text-xs text-zinc-500">
            No invoices yet — Stripe isn't wired to this user.
          </div>
        ) : (
          <table className="w-full text-xs">
            <tbody className="divide-y divide-zinc-800">
              {bill.invoices.map((i) => (
                <tr key={i.invoice_id}>
                  <td className="p-2 mono text-[11px]">{i.invoice_id}</td>
                  <td className="p-2">£{i.amount_gbp}</td>
                  <td className="p-2 text-zinc-400">{i.status}</td>
                  <td className="p-2 text-[10px] mono text-zinc-500">{i.created_at?.slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function CompsTab({ userId, onRevoke }) {
  const [comps, setComps] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/admin/users/${userId}/comps`).then((r) => {
      setComps(r.data);
      setLoading(false);
    });
  }, [userId]);
  const revoke = async (id) => {
    if (!window.confirm("Revoke this comp now?")) return;
    await api.post(`/admin/comps/${id}/revoke?reason=admin_manual`);
    setComps((c) => c.map((x) => (x.comp_id === id ? { ...x, status: "revoked" } : x)));
    onRevoke?.();
  };
  if (loading) return <div className="text-xs text-zinc-500">Loading…</div>;
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-[4px]" data-testid="comps-tab">
      {comps.length === 0 ? (
        <div className="p-3 text-xs text-zinc-500">No comps ever granted.</div>
      ) : (
        <table className="w-full text-xs">
          <thead className="bg-zinc-900">
            <tr className="mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
              <th className="text-left p-2">Comp plan</th>
              <th className="text-left p-2">Original</th>
              <th className="text-left p-2">Started</th>
              <th className="text-left p-2">Expires</th>
              <th className="text-left p-2">Reason</th>
              <th className="text-left p-2">Status</th>
              <th />
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {comps.map((c) => (
              <tr key={c.comp_id}>
                <td className="p-2 capitalize">{c.comp_plan}</td>
                <td className="p-2 text-zinc-400 capitalize">{c.original_plan}</td>
                <td className="p-2 mono text-[10px] text-zinc-500">{c.started_at?.slice(0, 10)}</td>
                <td className="p-2 mono text-[10px] text-zinc-500">{c.expires_at?.slice(0, 10)}</td>
                <td className="p-2 text-[11px]">{c.reason}</td>
                <td className="p-2 mono text-[10px] uppercase tracking-[0.14em]">
                  {c.status}
                </td>
                <td className="p-2 text-right">
                  {c.status === "active" && (
                    <button
                      onClick={() => revoke(c.comp_id)}
                      data-testid={`revoke-${c.comp_id}`}
                      className="text-[10px] text-rose-300 hover:text-rose-400"
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function NotesTab({ userId }) {
  const [notes, setNotes] = useState([]);
  const [draft, setDraft] = useState("");

  const load = async () => {
    const r = await api.get(`/admin/users/${userId}/notes`);
    setNotes(r.data);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const add = async () => {
    if (!draft.trim()) return;
    await api.post(`/admin/users/${userId}/notes`, {
      content_markdown: draft.trim(),
    });
    setDraft("");
    load();
  };

  return (
    <div data-testid="notes-tab">
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-[4px] p-3 mb-3">
        <div className="mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">
          Internal note (user never sees this)
        </div>
        <textarea
          rows={3}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          data-testid="note-draft"
          placeholder="Spoke with Sarah on Monday — VIP, loves the geo matrix"
          className="w-full bg-zinc-950 border border-zinc-800 rounded-[3px] px-2 py-1.5 text-xs"
        />
        <div className="mt-2 flex justify-end">
          <button
            onClick={add}
            disabled={!draft.trim()}
            data-testid="note-save"
            className="bg-white text-black text-xs px-3 py-1.5 rounded-[3px] hover:bg-zinc-200 disabled:opacity-40"
          >
            Save note
          </button>
        </div>
      </div>
      <div className="space-y-2">
        {notes.map((n) => (
          <div key={n.note_id} className="bg-zinc-900/60 border border-zinc-800 rounded-[4px] p-3">
            <div className="mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">
              {n.admin_name} · {shortDate(n.created_at)}
            </div>
            <div className="mt-1 text-xs whitespace-pre-wrap">
              {n.content_markdown}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ------- Reason prompt -------
function ReasonPrompt({ kind, onClose, onConfirm, userEmail }) {
  const [reason, setReason] = useState("");
  const [days, setDays] = useState(14);
  const [confirm, setConfirm] = useState("");
  const [tpl, setTpl] = useState("welcome_back");
  const label = {
    trial_extend: "Extend trial",
    lock: "Lock account",
    unlock: "Unlock account",
    reset_password: "Reset password (via email)",
    soft_delete: "Soft-delete account",
    impersonate: "Log in as this user",
    send_email: "Send email",
  }[kind];
  const extras = () => {
    if (kind === "trial_extend") return { days: Number(days) };
    if (kind === "soft_delete") return { confirm };
    if (kind === "send_email") return { template_id: tpl };
    return {};
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4" data-testid="reason-modal">
      <div className="bg-zinc-950 border border-zinc-800 rounded-[4px] max-w-md w-full p-4">
        <div className="display text-lg font-bold tracking-tight">{label}</div>
        <div className="text-xs text-zinc-400 mt-1">{userEmail}</div>
        <div className="mt-3 space-y-3">
          {kind === "trial_extend" && (
            <div>
              <label className="mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">Days (1–60)</label>
              <input
                type="number"
                min={1}
                max={60}
                value={days}
                onChange={(e) => setDays(e.target.value)}
                data-testid="trial-days"
                className="mt-1 w-full bg-zinc-950 border border-zinc-800 rounded-[3px] px-2 py-1.5 text-xs"
              />
            </div>
          )}
          {kind === "soft_delete" && (
            <div>
              <label className="mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                Type the user's email to confirm
              </label>
              <input
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="delete-confirm"
                className="mt-1 w-full bg-zinc-950 border border-zinc-800 rounded-[3px] px-2 py-1.5 text-xs"
              />
            </div>
          )}
          {kind === "send_email" && (
            <div>
              <label className="mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">Template</label>
              <select
                value={tpl}
                onChange={(e) => setTpl(e.target.value)}
                data-testid="email-template"
                className="mt-1 w-full bg-zinc-950 border border-zinc-800 rounded-[3px] px-2 py-1.5 text-xs"
              >
                <option value="welcome_back">welcome_back</option>
                <option value="plan_reminder">plan_reminder</option>
                <option value="custom">custom</option>
              </select>
            </div>
          )}
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
              Reason (required — logged to audit)
            </label>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="reason-input"
              className="mt-1 w-full bg-zinc-950 border border-zinc-800 rounded-[3px] px-2 py-1.5 text-xs"
            />
          </div>
        </div>
        <div className="mt-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-xs text-zinc-400 hover:text-white px-2">
            Cancel
          </button>
          <button
            disabled={!reason.trim()}
            onClick={() => onConfirm(reason.trim(), extras())}
            data-testid="reason-confirm"
            className="bg-white text-black text-xs px-3 py-1.5 rounded-[3px] hover:bg-zinc-200 disabled:opacity-40"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

// ------- bits -------
function PlanBadge({ plan, comp }) {
  const cls =
    {
      trial: "bg-zinc-800 text-zinc-300 border-zinc-700",
      starter: "bg-emerald-900/40 text-emerald-300 border-emerald-700",
      growth: "bg-sky-900/40 text-sky-300 border-sky-700",
      scale: "bg-amber-900/40 text-amber-300 border-amber-700",
      comp: "bg-purple-900/40 text-purple-300 border-purple-700",
      churned: "bg-rose-900/40 text-rose-300 border-rose-700",
    }[plan] || "bg-zinc-800 text-zinc-300 border-zinc-700";
  return (
    <span className={`text-[10px] mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-sm border ${cls}`}>
      {plan}
      {comp && <span className="ml-1 text-amber-400">★ comp</span>}
    </span>
  );
}

function StatusPill({ status }) {
  const cls =
    status === "active"
      ? "text-emerald-300 border-emerald-700 bg-emerald-900/20"
      : status === "locked"
      ? "text-amber-300 border-amber-700 bg-amber-900/20"
      : "text-rose-300 border-rose-700 bg-rose-900/20";
  return (
    <span className={`text-[10px] mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-sm border ${cls}`}>
      {status}
    </span>
  );
}

function shortDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function daysAgo(iso) {
  if (!iso) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
}
